import {useContext, useMemo} from 'react';

import {envIsPublicCloud} from '../../../config';
import {useLegacyMrProjectQuery} from '../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {propagateErrorsContext} from '../../../util/errors';
import {useRampFlagModelRegistryMigrationNotification} from '../../../util/rampFeatureFlags';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';

export const useIsModelRegistryMigrationNotificationEnabled = (): {
  enableMRMigrationNotification: boolean;
  enableMRMigrationNotificationLoading: boolean;
} => {
  // On Saas, personal entities are a part of personal organizations.
  // This feature flag should be disabled for personal entities.
  // However, on server, personal entities are a part of the `server`
  // organization, so the flag ends up getting set as true if the env var is set to true.
  // TODO: Explicitly handle the value returned for personal entities on Server.
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {
    data: enableMRMigrationNotification,
    loading: enableMRMigrationNotificationLoading,
  } = useRampFlagModelRegistryMigrationNotification(orgName ?? '');
  return useMemo(() => {
    return {
      enableMRMigrationNotification,
      enableMRMigrationNotificationLoading,
    };
  }, [enableMRMigrationNotification, enableMRMigrationNotificationLoading]);
};

export const useGetMigratedRegistryEntityInfo = (
  registryEntityName: string,
  registryProjectName: string
): {
  isMigratedRegistryProject: boolean;
  legacyMREntityName?: string;
  migrationCompletedAt?: string;
} => {
  const {data, loading} = useLegacyMrProjectQuery({
    variables: {
      orgEntityName: registryEntityName,
      registryProjectName,
    },
    context: propagateErrorsContext(),
  });
  let entityName = undefined;
  let migrationCompletedAt = undefined;
  let isMigratedRegistryProject = false;
  if (!loading) {
    const legacyMRProject = data?.project?.legacyModelRegistryProject;
    const orgEntity = data?.project?.entity;
    const migratedAt =
      orgEntity?.modelRegistryMigrationConfig?.migrationCompletedAt;
    if (migratedAt != null) {
      migrationCompletedAt = new Date(migratedAt).toString();
    }
    if (legacyMRProject != null) {
      entityName = legacyMRProject.entityName;
    }
    isMigratedRegistryProject =
      data?.project?.isMigratedRegistryProject ?? false;
  }
  return {
    isMigratedRegistryProject,
    legacyMREntityName: entityName,
    migrationCompletedAt,
  };
};
